import {
  Button,
  Group,
  TextInput as MantineTextInput,
  NumberInput,
  Paper,
  Pill,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute } from '@tanstack/react-router'
import { useDeferredValue, useState } from 'react'
import { useSongPath, useTextSearch } from '~/api/mutations'
import { useAlbums, useFingerprint, useSearch } from '~/api/queries'
import type { Track } from '~/api/types'
import { AlbumCard } from '~/components/blocks/AlbumGrid/AlbumGrid'
import { MusicMap } from '~/components/blocks/MusicMap/MusicMap'
import { SaveAsPlaylist } from '~/components/blocks/SaveAsPlaylist'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import { SkeletonList } from '~/components/primitives/SkeletonList'

export const Route = createFileRoute('/_app/discover/')({
  component: DiscoverPage,
})

const ALBUM_GRID_COLS = { base: 2, sm: 3, md: 4, lg: 6 }

function RandomAlbumsSection() {
  const { data, isLoading } = useAlbums({ sort: 'random', limit: 12 })

  return (
    <section>
      <Title order={3} mb="md">
        Random Albums
      </Title>
      {isLoading ? (
        <SimpleGrid cols={ALBUM_GRID_COLS}>
          <SkeletonList count={12} height={200} />
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={ALBUM_GRID_COLS}>
          {data?.data?.map((album) => (
            <AlbumCard key={album.id} album={album} />
          ))}
        </SimpleGrid>
      )}
    </section>
  )
}

function TrackPicker({
  label,
  selected,
  onSelect,
  onClear,
}: {
  label: string
  selected: { id: number; title: string; artist: string } | null
  onSelect: (track: { id: number; title: string; artist: string }) => void
  onClear: () => void
}) {
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const { data: searchData } = useSearch(deferredQuery, { limit: 10 })

  const results = (searchData?.tracks.data ?? []).filter((t) => t.id !== selected?.id)

  return (
    <div>
      <Text size="sm" fw={500} mb={4}>
        {label}
      </Text>
      {selected ? (
        <Pill.Group mb="xs">
          <Pill withRemoveButton onRemove={onClear}>
            {selected.artist} — {selected.title}
          </Pill>
        </Pill.Group>
      ) : (
        <>
          <MantineTextInput
            placeholder="Search for a track..."
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          {query && results.length > 0 && (
            <Paper withBorder mt={4} p="xs" mah={200} style={{ overflowY: 'auto' }}>
              <Stack gap={2}>
                {results.map((t) => (
                  <Button
                    key={t.id}
                    variant="subtle"
                    size="compact-sm"
                    justify="flex-start"
                    fullWidth
                    onClick={() => {
                      onSelect({ id: t.id, title: t.title, artist: t.artist })
                      setQuery('')
                    }}
                  >
                    {t.artist} — {t.title} ({t.album})
                  </Button>
                ))}
              </Stack>
            </Paper>
          )}
          {query && deferredQuery && results.length === 0 && searchData && (
            <Text size="xs" c="dimmed" mt={4}>
              No matching tracks found.
            </Text>
          )}
        </>
      )}
    </div>
  )
}

function TextSearchTab() {
  const textSearch = useTextSearch()
  const [query, setQuery] = useState('')
  const [limit, setLimit] = useState(20)
  const [results, setResults] = useState<Track[] | null>(null)

  const onSearch = async () => {
    if (!query.trim()) return
    try {
      const data = await textSearch.mutateAsync({ query: query.trim(), limit })
      setResults(data.tracks)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Text search failed.'
      notifications.show({ title: 'Error', message, color: 'red' })
    }
  }

  return (
    <Stack gap="md">
      <Paper
        withBorder
        p="lg"
        maw={600}
        component="form"
        onSubmit={(e: { preventDefault: () => void }) => {
          e.preventDefault()
          onSearch()
        }}
      >
        <Stack gap="md">
          <MantineTextInput
            label="Query"
            placeholder="upbeat jazz with saxophone"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <NumberInput
            label="Limit"
            min={1}
            max={100}
            value={limit}
            onChange={(v) => setLimit(typeof v === 'number' ? v : 20)}
          />
          <Button type="submit" loading={textSearch.isPending}>
            Search
          </Button>
        </Stack>
      </Paper>
      {results && (
        <>
          <Group>
            <SaveAsPlaylist tracks={results} defaultName={query} />
          </Group>
          <TrackTable tracks={results} />
        </>
      )}
    </Stack>
  )
}

function SongPathTab() {
  const songPathMutation = useSongPath()
  const [from, setFrom] = useState<{ id: number; title: string; artist: string } | null>(null)
  const [to, setTo] = useState<{ id: number; title: string; artist: string } | null>(null)
  const [steps, setSteps] = useState(10)
  const [results, setResults] = useState<Track[] | null>(null)

  const onFindPath = async () => {
    if (!from || !to) {
      notifications.show({
        title: 'Error',
        message: 'Select both a start and end track.',
        color: 'red',
      })
      return
    }
    try {
      const data = await songPathMutation.mutateAsync({
        fromTrackId: from.id,
        toTrackId: to.id,
        steps,
      })
      setResults(data.tracks)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Song path failed.'
      notifications.show({ title: 'Error', message, color: 'red' })
    }
  }

  return (
    <Stack gap="md">
      <Paper
        withBorder
        p="lg"
        maw={600}
        component="form"
        onSubmit={(e: { preventDefault: () => void }) => {
          e.preventDefault()
          onFindPath()
        }}
      >
        <Stack gap="md">
          <TrackPicker
            label="Start Track"
            selected={from}
            onSelect={setFrom}
            onClear={() => setFrom(null)}
          />
          <TrackPicker
            label="End Track"
            selected={to}
            onSelect={setTo}
            onClear={() => setTo(null)}
          />
          <NumberInput
            label="Steps"
            min={1}
            max={50}
            value={steps}
            onChange={(v) => setSteps(typeof v === 'number' ? v : 10)}
          />
          <Button type="submit" loading={songPathMutation.isPending}>
            Find Path
          </Button>
        </Stack>
      </Paper>
      {results && (
        <>
          <Group>
            <SaveAsPlaylist
              tracks={results}
              defaultName={
                from && to ? `${from.artist} — ${from.title} → ${to.artist} — ${to.title}` : ''
              }
            />
          </Group>
          <TrackTable tracks={results} />
        </>
      )}
    </Stack>
  )
}

function FingerprintTab() {
  const [days, setDays] = useState(30)
  const [limit, setLimit] = useState(20)
  const [enabled, setEnabled] = useState(false)
  const { data, isLoading, error, refetch } = useFingerprint({ days, limit }, enabled)

  const onGetRecommendations = () => {
    if (enabled) {
      refetch()
    } else {
      setEnabled(true)
    }
  }

  return (
    <Stack gap="md">
      <Paper
        withBorder
        p="lg"
        maw={600}
        component="form"
        onSubmit={(e: { preventDefault: () => void }) => {
          e.preventDefault()
          onGetRecommendations()
        }}
      >
        <Stack gap="md">
          <NumberInput
            label="Days of history"
            min={1}
            max={365}
            value={days}
            onChange={(v) => {
              setDays(typeof v === 'number' ? v : 30)
              setEnabled(false)
            }}
          />
          <NumberInput
            label="Limit"
            min={1}
            max={100}
            value={limit}
            onChange={(v) => {
              setLimit(typeof v === 'number' ? v : 20)
              setEnabled(false)
            }}
          />
          <Button type="submit" loading={isLoading}>
            Get Recommendations
          </Button>
        </Stack>
      </Paper>
      {isLoading && <Skeleton height={200} />}
      {error && (
        <Text c="red" size="sm">
          {error.message}
        </Text>
      )}
      {data?.tracks && (
        <>
          <Group>
            <SaveAsPlaylist tracks={data.tracks} defaultName="Fingerprint recommendations" />
          </Group>
          <TrackTable tracks={data.tracks} />
        </>
      )}
    </Stack>
  )
}

function DiscoverPage() {
  return (
    <Stack gap="md">
      <Title order={2}>Discover</Title>

      <Tabs defaultValue="text-search">
        <Tabs.List>
          <Tabs.Tab value="text-search">Text Search</Tabs.Tab>
          <Tabs.Tab value="song-path">Song Path</Tabs.Tab>
          <Tabs.Tab value="fingerprint">Fingerprint</Tabs.Tab>
          <Tabs.Tab value="music-map">Music Map</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="text-search" pt="md">
          <TextSearchTab />
        </Tabs.Panel>
        <Tabs.Panel value="song-path" pt="md">
          <SongPathTab />
        </Tabs.Panel>
        <Tabs.Panel value="fingerprint" pt="md">
          <FingerprintTab />
        </Tabs.Panel>
        <Tabs.Panel value="music-map" pt="md">
          <MusicMap />
        </Tabs.Panel>
      </Tabs>

      <RandomAlbumsSection />
    </Stack>
  )
}
